import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { Helmet } from "react-helmet";
import { BRANDS } from "app/constants";
import { getBrandDisplayName } from "app/reducers/partnerSelector";
import uniqBy from "lodash/uniqBy";
import flattenDeep from "lodash/flattenDeep";
import { useLocation } from "react-router-dom";
import appGlobalsContext from "app/AppGlobalsContext";
import { useContext } from "react";
import { parseQueries } from "app/utils/routerUtils";

const getAlternates = ({ path, domain, proxyBasenamePrefix, queryParam, shops = [], brand }) => {
	let hasEnWWShop;

	const domainsByShop = shops.map((shopData = {}) => {
		const marketingCodes = shopData.marketingCodes || [];
		const domainsByMarketingCodes = marketingCodes.map(marketingCode => {
			const shop = `${marketingCode.locale}-${shopData.sellingCountry}`;
			const isGB = shop === "en-GB";

			let hreflangValue = isGB ? "en" : shop;

			// es-ES => es
			if (marketingCode.locale.toUpperCase() === shopData.sellingCountry) {
				hreflangValue = marketingCode.locale;
			}

			if (shop === "en-WW") {
				hasEnWWShop = true;
			}

			return {
				rel: "alternate",
				hreflang: hreflangValue,
				href:
					brand === BRANDS.PS && shop !== "fr-FR"
						? `https://booking.perfectstay.com/${shop}${path}${queryParam}`
						: `${domain}${proxyBasenamePrefix}/${shop}${path}${queryParam}`,
			};
		});

		return domainsByMarketingCodes;
	});

	if (hasEnWWShop) {
		domainsByShop.push([
			{
				rel: "alternate",
				hreflang: "x-default",
				href:
					brand === BRANDS.PS
						? `https://booking.perfectstay.com/en-WW`
						: `${domain}${proxyBasenamePrefix}/en-WW`,
			},
		]);
	}

	return uniqBy(flattenDeep(domainsByShop), domain => domain.hreflang + domain.href);
};

const isKeyStringIntl = str => {
	const regex = /^[a-z0-9]+(\.[a-z0-9]+)+$/;
	return regex.test(str);
};

const HeadMetadata = ({
	brandDisplayName,
	shops = [],
	prefetchRessources = [],
	title,
	titleAttributes = {},
	robotsMetaTag = "",
	imageUrl = "",
	description,
	descriptionAttributes = {},
	children,
	actualDomainFromAWS,
}) => {
	const intl = useIntl();

	const { brand, shop, envVars } = useContext(appGlobalsContext);

	const proxyBasenamePrefix = envVars.PROXY_BASENAME_PREFIX;

	const environment = envVars.ENVIRONMENT;

	const { pathname, search } = useLocation();

	const query = parseQueries(search);

	const computedPathname = pathname === "/" ? "" : pathname;

	const queryParam =
		computedPathname === "/merch" && query.code
			? `?code=${encodeURIComponent(query.code)}`
			: "";

	const alternates = getAlternates({
		path: computedPathname,
		domain: actualDomainFromAWS,
		proxyBasenamePrefix,
		queryParam,
		shops,
		brand,
	});

	return (
		<Helmet>
			{title && (
				<title>
					{isKeyStringIntl(title)
						? intl.formatMessage(
								{ id: title },
								{ siteTitle: brandDisplayName, ...titleAttributes }
						  )
						: title}
				</title>
			)}

			{title && (
				<meta
					property="og:title"
					content={
						isKeyStringIntl(title)
							? intl.formatMessage(
									{ id: title },
									{ siteTitle: brandDisplayName, ...titleAttributes }
							  )
							: title
					}
				/>
			)}

			{description && (
				<meta
					name="description"
					content={
						isKeyStringIntl(description)
							? intl.formatMessage(
									{ id: description },
									{ siteTitle: brandDisplayName, ...descriptionAttributes }
							  )
							: description
					}
				/>
			)}

			{description && (
				<meta
					property="og:description"
					content={
						isKeyStringIntl(description)
							? intl.formatMessage(
									{ id: description },
									{ siteTitle: brandDisplayName, ...descriptionAttributes }
							  )
							: description
					}
				/>
			)}

			{imageUrl && <meta property="og:image" content={imageUrl} />}

			{imageUrl && <meta name="thumbnail" content={imageUrl} />}

			{robotsMetaTag && (
				<meta
					name="robots"
					content={environment?.includes("live") ? robotsMetaTag : "noindex, nofollow"}
				/>
			)}

			<link
				rel="canonical"
				href={
					brand === BRANDS.PS && shop !== "fr-FR"
						? `https://booking.perfectstay.com/${shop}${computedPathname}`
						: `${actualDomainFromAWS}${proxyBasenamePrefix}/${shop}${computedPathname}${queryParam}`
				}
			/>

			{alternates.map(({ hreflang, href }, index) => {
				return <link key={index} rel="alternate" hrefLang={hreflang} href={href} />;
			})}

			{prefetchRessources.map((resourceUrl, index) => {
				return <link key={index} rel="preload" as="image" href={resourceUrl} />;
			})}

			{children}
		</Helmet>
	);
};

HeadMetadata.propTypes = {
	brandDisplayName: PropTypes.string,
	shops: PropTypes.array,
	title: PropTypes.string,
	titleAttributes: PropTypes.object,
	robotsMetaTag: PropTypes.string,
	imageUrl: PropTypes.string,
	description: PropTypes.string,
	actualDomainFromAWS: PropTypes.string,
	descriptionAttributes: PropTypes.object,
	prefetchRessources: PropTypes.arrayOf(PropTypes.string),
};

const mapStateToProps = state => {
	return {
		brandDisplayName: getBrandDisplayName(state),
		shops: state.brand.shops,
		actualDomainFromAWS: state.actualDomainFromAWS,
	};
};

export default connect(mapStateToProps)(HeadMetadata);
